import * as React from "react";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import {
  IMobileOrchestrator,
  IMobileReactivationOrchestrator,
  IMultiOrderOrchestrator,
} from "@nju/data-access-portal-orchestrator";
import {
  IServiceStatus,
  useMemberProducts,
} from "@nju/data-access-portal-product";
import { portalRoutes } from "@nju/routes";
import { Panel, Separator, Text } from "@nju/ui";
import { Buttons } from "./Buttons";
import { getMobileServiceCTABannerConditions } from "./getMobileServiceCTABannerConditions";
import { getMobileStatusData } from "./getMobileStatusData";
import { mobileServiceCTABannerMachine } from "./mobileServiceCTABanner.machine";

interface IServiceCTABannerProps {
  offerId: string;
  serviceStatus: IServiceStatus["mobileServiceStatus"];
  orchestrator: IMobileOrchestrator | IMultiOrderOrchestrator | undefined;
  reactivationOrchestratorState:
    | IMobileReactivationOrchestrator["state"]
    | undefined;
}

export function MobileServiceCTABanner({
  offerId,
  serviceStatus,
  orchestrator,
  reactivationOrchestratorState,
}: IServiceCTABannerProps) {
  const { reload, push } = useRouter();
  const { mobileProducts: memberProducts } = useMemberProducts();
  const productId = memberProducts.mobileAccess?.id;

  const [current, send] = useMachine(mobileServiceCTABannerMachine, {
    context: {
      productId,
      currentStatus: serviceStatus,
    },
    actions: {
      onCancelServiceResignationSuccess: () => {
        reload();
      },
      onCancelServiceResignationError: () => {
        push(portalRoutes.ups.get());
      },
    },
  });
  const { matches } = current;

  const refresh = React.useCallback(() => {
    reload();
  }, [reload]);

  const handleCancelServiceResignation = React.useCallback(() => {
    send({ type: "CANCEL_SERVICE_RESIGNATION" });
  }, [send]);

  const isCancelServiceResignationLoading = matches("cancelServiceResignation");

  const conditions = React.useMemo(() => {
    return getMobileServiceCTABannerConditions({
      serviceStatus,
      orchestrator,
      reactivationOrchestratorState,
    });
  }, [serviceStatus, orchestrator, reactivationOrchestratorState]);

  const panelProps = React.useMemo(() => {
    return getMobileStatusData({
      conditions,
      mobileAccess: memberProducts.mobileAccess,
    });
  }, [conditions, memberProducts.mobileAccess]);

  if (!panelProps) {
    // eslint-disable-next-line unicorn/no-null
    return null;
  }

  const { text, title, iconName } = panelProps;

  return (
    <>
      <Panel
        iconName={iconName}
        testId="mobile-service-cta-banner"
        title={title}
      >
        <Separator size="xs" tone="invisible" />
        <Text
          size={{
            "@onlyMobile": "m",
            "@fromTablet": "l",
          }}
        >
          {text}
        </Text>
        <Separator size="xs" tone="invisible" />
        <Buttons
          conditions={conditions}
          handleCancelServiceResignation={handleCancelServiceResignation}
          isCancelServiceResignationLoading={isCancelServiceResignationLoading}
          mobileAccess={memberProducts.mobileAccess}
          offerId={offerId}
          refreshCallback={refresh}
          userId={orchestrator?.userId}
        />
      </Panel>
      <Separator size="xs" tone="invisible" />
    </>
  );
}
